import React, { useCallback } from "react";

import { RunIgnoredTrigger, VcsProvider } from "types/generated";
import { Commit } from "components/icons/generated";
import Counter from "ds/components/Counter";
import MetaInfoListItem from "components/MetaInfoList/Item";
import useTypedContext from "hooks/useTypedContext";
import { StackContext } from "views/Stack/Context";
import Box from "ds/components/Box";
import { useToggle } from "hooks/useToggle";
import { getCommitLabel } from "utils/commit";
import TimestampMetaInfo from "components/Timestamp/MetaInfo";
import CardCollapsible from "ds/components/Card/Collapsible";
import CardTitle from "ds/components/Card/components/Title";
import { getRepoRevisionUrl } from "utils/spaceliftRepo";
import { providerHasVcsHost } from "utils/vcs";

import styles from "./styles.module.css";
import StackIgnoredRunsGroupCommitAuthorLink from "./CommitAuthorLink";

type StackIgnoredRunsGroupProps = {
  run: RunIgnoredTrigger;
  provider: VcsProvider;
  count: number;
  isOpen?: boolean;
  children?: React.ReactNode;
};

const StackIgnoredRunsGroup = (props: StackIgnoredRunsGroupProps) => {
  const { run, provider, children, count, isOpen = false } = props;
  const [isCollapsed, toggle] = useToggle(!isOpen);

  const { stack } = useTypedContext(StackContext);

  const handleToggle = useCallback(() => {
    toggle();
  }, [toggle]);

  const commitUrl =
    provider === VcsProvider.Spacelift && stack.vcsIntegration
      ? getRepoRevisionUrl(stack.vcsIntegration.id, run.commit.hash)
      : run.commit.url;

  const hasVcsHost = providerHasVcsHost(provider);
  const commitLabel = getCommitLabel(provider, run.commit.hash);
  const commitTooltip = stack.trackedCommit?.hash === run.commit.hash ? "Tracked commit" : null;

  return (
    <CardCollapsible
      fullbleed
      headerAlign="start"
      ariaLevel={3}
      ariaLabel={run.commit.message}
      isCollapsed={isCollapsed}
      onToggle={handleToggle}
      title={
        <Box direction="column" gap="medium" fullWidth>
          <Box gap="medium" gridArea="commit" align="center">
            <CardTitle tag="span">{run.commit.message}</CardTitle>
            <Counter count={count} />
          </Box>

          <Box gridArea="meta" gap="large">
            <TimestampMetaInfo
              prefix="Committed on"
              timestamp={run.commit.timestamp}
              postfix={
                <>
                  by&nbsp;
                  <StackIgnoredRunsGroupCommitAuthorLink
                    login={run.commit.authorLogin || undefined}
                    name={run.commit.authorName}
                    provider={provider}
                  />
                </>
              }
            />

            {!hasVcsHost && (
              <MetaInfoListItem icon={Commit} tooltip={commitTooltip}>
                {commitLabel}
              </MetaInfoListItem>
            )}
            {hasVcsHost && (
              <MetaInfoListItem
                href={commitUrl}
                linkText={commitLabel}
                icon={Commit}
                target="_blank"
                tooltip={commitTooltip}
              />
            )}
          </Box>
        </Box>
      }
    >
      <div className={styles.wrapper}>{children}</div>
    </CardCollapsible>
  );
};

export default StackIgnoredRunsGroup;
